/**
 * Select the Kuali modules copied by one Workspace Vault feed.
 *
 * USM shares the Research tenant, so these compact choices only add module
 * configuration tabs. The backend independently verifies every selected or
 * linked record belongs to UMCP before the feed can transfer attachments.
 */
import { Checkbox } from '@/components/ui'
import { cn } from '@/lib/utils'
import type { KualiResearchSourceType } from '../types'

export const KUALI_RESEARCH_SOURCES = [
  {
    value: 'awards',
    label: 'Awards',
    description: 'Current award agreements, modifications, reports, and budgets.',
    documents: 'Selected official Award attachment categories.',
    metadata: 'The complete active Award form, including terms, reports, comments, custom data, and people exposed by Kuali.',
  },
  {
    value: 'subawards',
    label: 'Subawards',
    description: 'Agreements, amendments, flow-downs, reports, and closeout files.',
    documents: 'All eligible documents attached to each linked Subaward.',
    metadata: 'The complete authorized Subaward details returned by Kuali.',
  },
  {
    value: 'negotiations',
    label: 'Negotiations',
    description: 'Drafts, redlines, correspondence, NDAs, MTAs, and DUAs.',
    documents: 'All eligible attachments from allowed Negotiation activities.',
    metadata: 'The Negotiation details and allowed activities returned by Kuali.',
  },
  {
    value: 'institutional-proposals',
    label: 'Institutional Proposals',
    description: 'Submitted proposals, SOWs, sponsor documents, and commitments.',
    documents: 'All eligible attachments from the linked active Institutional Proposal.',
    metadata: 'The active Institutional Proposal details returned by Kuali.',
  },
  {
    value: 'proposal-development',
    label: 'Proposal Development',
    description: 'Proposal narratives and related files; personnel biographies are excluded.',
    documents: 'All eligible narrative files; personnel biographies are excluded.',
    metadata: 'The Development Proposal and its narrative details returned by Kuali.',
  },
] as const satisfies readonly {
  value: KualiResearchSourceType
  label: string
  description: string
  documents: string
  metadata: string
}[]

export const ALL_KUALI_RESEARCH_SOURCES = KUALI_RESEARCH_SOURCES.map(
  source => source.value,
)

export function KualiResearchSourceSelection({
  selected,
  onSelectedChange,
}: {
  selected: KualiResearchSourceType[]
  onSelectedChange: (sources: KualiResearchSourceType[]) => void
}) {
  const toggle = (source: KualiResearchSourceType, checked: boolean) => {
    onSelectedChange(checked
      ? [...selected, source]
      : selected.filter(value => value !== source))
  }

  return (
    <fieldset className="space-y-2">
      <legend className="sr-only">Research modules</legend>
      <div className="flex flex-wrap gap-[7px]">
        {KUALI_RESEARCH_SOURCES.map(source => {
          const checked = selected.includes(source.value)
          return (
            <label
              key={source.value}
              title={source.description}
              className={cn(
                'inline-flex h-8 cursor-pointer items-center gap-[7px] rounded-lg border pl-2 pr-3 text-[12.5px] font-medium text-slate-900 transition-colors',
                checked
                  ? 'border-slate-300 bg-slate-50'
                  : 'border-slate-200 bg-white hover:bg-slate-50',
              )}
            >
              <Checkbox
                aria-label={source.label}
                checked={checked}
                disabled={checked && selected.length === 1}
                onCheckedChange={value => toggle(source.value, value === true)}
                className="h-[15px] w-[15px] rounded border-[1.5px] bg-white data-[state=checked]:border-slate-800 data-[state=checked]:bg-slate-800"
              />
              {source.label}
            </label>
          )
        })}
      </div>
      <p className="text-xs text-slate-400">
        Select one or more. Every enabled module gets its own settings card and becomes available in agent document mapping.
      </p>
    </fieldset>
  )
}
